import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiHeart, FiImage } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import userApi from '../../api/userApi';
import { DEFAULT_PRODUCT_IMAGE } from '../../utils/constants';

const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const [isInWishlist, setIsInWishlist] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Add handleProductClick function
  const handleProductClick = () => {
    navigate(`/product/${product.id}`);
  };

  // Check initial wishlist status
  useEffect(() => {
    const checkWishlistStatus = async () => {
      try {
        const wishlistData = await userApi.getWishlist();
        const isInList = wishlistData.some(item => item.product?.id === product.id);
        setIsInWishlist(isInList);
      } catch (error) {
        console.error('Error checking wishlist status:', error);
      }
    };

    checkWishlistStatus();
  }, [product.id]);

  // Listen for wishlist updates
  useEffect(() => {
    const handleWishlistUpdate = async () => {
      const wishlistData = await userApi.getWishlist();
      const isInList = wishlistData.some(item => item.product?.id === product.id);
      setIsInWishlist(isInList);
    };

    window.addEventListener('wishlistUpdated', handleWishlistUpdate);
    return () => window.removeEventListener('wishlistUpdated', handleWishlistUpdate);
  }, [product.id]);

  const toggleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      if (isInWishlist) {
        // Find the wishlist item ID first
        const wishlistData = await userApi.getWishlist();
        const wishlistItem = wishlistData.find(item => item.product?.id === product.id);
        
        if (wishlistItem) {
          await userApi.removeFromWishlist(product.id);
          setIsInWishlist(false);
          toast.success('Removed from wishlist');
        }
      } else {
        await userApi.addToWishlist(product.id);
        setIsInWishlist(true);
        toast.success('Added to wishlist');
      }
      
      // Dispatch event to update other components
      window.dispatchEvent(new Event('wishlistUpdated'));
    } catch (error) {
      if (error.message.includes('login')) {
        navigate('/login');
        return;
      }
      
      // Handle specific error cases
      if (error.message.includes('400') || error.message.includes('already exists')) {
        // If item already exists in wishlist, switch to remove operation
        try {
          const wishlistData = await userApi.getWishlist();
          const wishlistItem = wishlistData.find(item => item.product?.id === product.id);
          
          if (wishlistItem) {
            await userApi.removeFromWishlist(product.id);
            setIsInWishlist(false);
            toast.success('Removed from wishlist');
            window.dispatchEvent(new Event('wishlistUpdated'));
          }
        } catch (removeError) {
          console.error('Error removing item:', removeError);
          toast.error('Failed to update wishlist');
        }
      } else {
        toast.error(error.message || 'Failed to update wishlist');
      }
    }
  };

  return (
    <motion.div
      onClick={handleProductClick}
      whileHover={{ y: -5, transition: { duration: 0.2 } }}
      className="cursor-pointer overflow-hidden transition-all duration-300 group bg-transparent hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)]"
    >
      <div className="relative aspect-w-3 aspect-h-4">
        {/* Offer Tag */}
        {product.activeOffers?.length > 0 && (
          <div className="absolute top-2 left-2 z-10">
            <div className="bg-red-500 text-white px-2 py-1 rounded-md text-xs font-medium shadow-sm">
              {product.activeOffers[0].offer_type === 'PERCENTAGE' 
                ? `${product.activeOffers[0].discount_value}% OFF`
                : `₹${product.activeOffers[0].discount_value} OFF`
              }
            </div>
          </div>
        )}
        
        {!imageError ? (
          <img
            src={product.image || product.images?.[0]?.url || DEFAULT_PRODUCT_IMAGE}
            alt={product.name}
            className="w-full h-full object-cover"
            onError={() => {
              console.error('Failed to load image:', product.image);
              setImageError(true);
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100">
            <FiImage className="w-12 h-12 text-gray-400" />
          </div>
        )}
      </div>
      <div className="p-4 bg-transparent group-hover:bg-white transition-colors duration-300">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">{product.name}</h3>
            <p className="text-gray-500">{product.brand}</p>
          </div>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={toggleWishlist}
            className={`
              p-2 rounded-full transition-all duration-300
              ${isInWishlist 
                ? 'bg-red-50 text-red-500 shadow-sm' 
                : 'text-gray-600 hover:text-[#2C3E50]'
              }
            `}
            title={isInWishlist ? 'Remove from Wishlist' : 'Add to Wishlist'}
          >
            <FiHeart 
              className={`w-4 h-4 transition-colors duration-300 ${
                isInWishlist ? 'fill-current' : ''
              }`} 
            />
          </motion.button>
        </div>
        <div className="mt-2 flex items-center justify-between">
          <div className="text-gray-900 font-medium">
            {product.activeOffers?.length > 0 ? (
              <>
                <span className="text-red-600">₹{product.finalPrice}</span>
                <span className="ml-2 text-sm text-gray-500 line-through">
                  ₹{product.price}
                </span>
              </>
            ) : product.salePrice ? (
              <>
                <span className="text-red-600">₹{product.salePrice}</span>
                <span className="ml-2 text-sm text-gray-500 line-through">
                  ₹{product.price}
                </span>
              </>
            ) : (
              <span>₹{product.price}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {product.discount > 0 && (
              <span className="text-xs font-medium text-green-600">
                {product.discount}% OFF
              </span>
            )}
            {!product.stock && (
              <span className="text-xs font-medium text-red-600">
                Out of Stock
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ProductCard;






































